<script setup lang="ts">
import AllInvoicesTable from '@/components/common/invoices/tables/all-invoices-table/all-invoices-table.vue';
import PaidInvoiceTable from '@/components/common/invoices/tables/paid-invoices-table/paid-invoice-table.vue';
import SelectableInvoiceTable from '@/components/common/invoices/tables/selectable-invoices-table/selectable-invoice-table.vue';
import SpecificInvoiceTable from '@/components/common/invoices/tables/specific-invoice-table/specific-invoice-table.vue';
import type { PaginationQueryState } from '@/components/tables/pagination-table/types';
import { RouteName } from '@/router/models/route-name.ts';
import { adminSupplierInvoiceService } from '@/services/admin/admin-supplier-invoice-service.ts';
import { paymentFileService } from '@/services/payment-file/payment-file-service';
import { voucherService } from '@/services/voucher/voucher-service';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import { PhDownloadSimple, PhEnvelope, PhPlus } from '@phosphor-icons/vue';
import { useQuery } from '@tanstack/vue-query';
import { fmtCurrency, type models } from 'cesg-shared';
import { SupplierInvoiceStatusType } from 'cesg-shared/dist/models';
import { computed, onActivated, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import CESGTable, { type ColumnDef } from '../booked-invoices/CESGTable.vue';
import InvoiceProcessingPanel, {
  type InvoiceProcessingPanelInvoice,
} from '../booked-invoices/InvoiceProcessingPanel.vue';
import FortnoxAccountHandler from '../booked-invoices/fortnox/FortnoxAccountHandler.vue';
import AdminInvoiceInbox from '../invoice-inbox/AdminInvoiceInbox.vue';
import AdminSupplierInvoiceExportModal from './AdminSupplierInvoiceExportModal.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

interface HideProps {
  mailboxSync?: boolean;
  invoiceProcessing?: boolean;
  fortnoxAccountHandler?: boolean;
}

interface FilterProps {
  supplier?: string;
}

const props = withDefaults(
  defineProps<{
    hide?: HideProps;
    filter?: FilterProps;
  }>(),
  {
    hide: () => ({
      mailboxSync: false,
      invoiceProcessing: false,
      fortnoxAccountHandler: false,
    }),
  },
);

interface Invoice {
  id: string;
  project: {
    id: string;
    fortnoxId: string;
    name: string;
  };
  accounts: { number: number; name: string }[];
  invoiceNumber: string;
  status: string;
  supplier: string;
  totalAmount: number;
  vat: number;
  invoiceDate: string;
  dueDate: string;
  approver: string;
  serialNumber: string;
  booked: boolean;
  paymentFileId: number | null;
  finalPayDate: string | null;
}

type BookedFilterStatus = 'approved' | 'verified-debt';
type FilterStatus = SupplierInvoiceStatusType | 'verified-debt' | 'all';

// ─── Regular invoice state ────────────────────────────────────────────────────
const activeFilter = ref<FilterStatus>(route.query.status as FilterStatus);
const showExportModal = ref(false);

const searchInput = ref<string>('');
const searchQuery = ref<string>('');

watch(searchInput, (val) => {
  const timer = setTimeout(() => {
    searchQuery.value = val;
  }, 300);
  return () => clearTimeout(timer);
});

// ─── Booked invoice state ─────────────────────────────────────────────────────
const selectedIds = ref<Set<string>>(new Set());
const isProcessing = ref(false);
const processedCount = ref(0);
const failedIds = ref<Map<string, string | undefined>>(new Map());
const succeededIds = ref<Map<string, string | undefined>>(new Map());
const processedInvoices = ref<Map<string, Invoice>>(new Map());
const isProcessingDone = ref(false);
const isRetrying = ref(false);
const retryingIds = ref<Set<string>>(new Set());
const paymentDate = ref<string>('');
const isDownloading = ref(false);
const isPaymentFileDownloaded = ref(false);
const isPaymentConfirmed = ref(false);
const paymentFileId = ref('');

interface ProperVoucherResult {
  message: string;
  voucher: models.FortnoxVoucher;
}

const isBookedFilter = computed(
  () =>
    activeFilter.value === 'approved' || activeFilter.value === 'verified-debt',
);

interface TableFilters {
  page: number;
  pageSize: number;
  sortColumnKey?: string;
  sortOrder?: 'asc' | 'desc';
}
const tableFilters = ref<TableFilters>({
  page: 1,
  pageSize: 50,
});

const currentTableQuery = computed<PaginationQueryState>(() => ({
  page: tableFilters.value.page,
  pageSize: tableFilters.value.pageSize,
  sortColumnKey: tableFilters.value.sortColumnKey ?? null,
  sortDirection: tableFilters.value.sortOrder,
}));

function onTableQueryChange(action: { payload: PaginationQueryState }) {
  const { payload } = action;
  tableFilters.value = {
    ...tableFilters.value,
    page: payload.page,
    pageSize: payload.pageSize,
    sortColumnKey: payload.sortColumnKey ?? undefined,
    sortOrder: payload.sortDirection,
  };
}

const {
  data: rawInvoices,
  isFetching,
  refetch,
} = useQuery({
  queryKey: computed(() => [
    'adminSupplierInvoices',
    'getFilteredList',
    tableFilters.value,
    activeFilter.value,
    searchQuery.value,
    props.filter?.supplier,
  ]),
  queryFn: async () => {
    const data = await adminSupplierInvoiceService.getFilteredList({
      status: activeFilter.value === 'all' ? undefined : activeFilter.value,
      limit: tableFilters.value.pageSize,
      page: tableFilters.value.page,
      sortKey: tableFilters.value.sortColumnKey as keyof NonNullable<
        typeof rawInvoices.value
      >['invoices'][number],
      sortOrder: tableFilters.value.sortOrder,
      query: searchQuery.value,
      supplier: props.filter?.supplier,
    });
    return data;
  },
  placeholderData: (previousData, previousQuery) => {
    const prevKey = previousQuery?.queryKey as unknown[] | undefined;
    // Only keep previous data if the status filter hasn't changed
    return prevKey?.[3] === activeFilter.value ? previousData : undefined;
  },
});

const totalInvoices = computed(() => rawInvoices.value?.total ?? 0);

const invoices = computed(
  () =>
    rawInvoices.value?.invoices.map((item): Invoice => {
      const mapped: Invoice = {
        id: item.id,
        serialNumber: item.serialNumber,
        project: item.project
          ? {
              id: item.project.id,
              name: item.project.name,
              fortnoxId: item.project.number,
            }
          : { id: '', name: '', fortnoxId: '' },
        accounts: item.accounts.map((a) => ({
          number: a.number,
          name: a.name,
        })),
        invoiceNumber: item.invoiceNumber ?? '',
        status: item.status,
        supplier: item.supplier?.name ?? '',
        totalAmount: item.totalAmount,
        vat: item.vat,
        invoiceDate: item.invoiceDate,
        dueDate: item.dueDate,
        approver: item.approver ?? '',
        booked: item.booked,
        paymentFileId: item.paymentFileId ?? null,
        finalPayDate: item.finalPayDate ?? null,
      };
      // Paid but unbooked still awaits its payment voucher, so it belongs here.
      if (
        (mapped.status === 'approved' && mapped.booked === true) ||
        (mapped.status === 'paid' && !mapped.booked)
      ) {
        mapped.status = 'verified-debt';
      }
      return mapped;
    }) ?? [],
);

type AdminInvoicesData = Invoice[];
type AdminInvoice = Invoice;

// ─── Regular invoice logic ────────────────────────────────────────────────────
const navigateToProjectInvoice = (invoiceId: string) => {
  const invoice = invoices.value.find((inv) => inv.id === invoiceId);

  if (!invoice) {
    return;
  }

  router.push({
    name: RouteName.InvoiceDetails,
    params: { id: invoice.project.id, invoiceId: invoiceId },
    query: {
      accountNumber: route.query.accountNumber ?? undefined,
      ataNumber: route.query.ataNumber ?? undefined,
    },
  });
};

const navigateToInvoice = (invoiceId: string) => {
  const invoice = invoices.value.find((inv) => inv.id === invoiceId);

  if (!invoice) {
    return;
  }

  router.push({
    name: RouteName.AdminEditInvoice,
    params: { id: invoiceId },
  });
};

const setFilter = (status: FilterStatus) => {
  if (status !== activeFilter.value) {
    activeFilter.value = status;
    tableFilters.value = { ...tableFilters.value, page: 1 };
    selectedIds.value = new Set();
    // Use history.replaceState instead of router.replace so the router's
    // scrollBehavior (which always scrolls to top) is not triggered.
    const url = new URL(window.location.href);
    url.searchParams.set('status', status);
    window.history.replaceState(window.history.state, '', url.toString());
  }
};

const fmtStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    incoming: t('invoiceStatus.incoming'),
    undercertificate: t('invoiceStatus.undercertificate'),
    paid: t('invoiceStatus.paid'),
    parked: t('invoiceStatus.parked'),
    disputed: t('invoiceStatus.disputed'),
    voided: t('invoiceStatus.voided'),
    approved: t('invoiceStatus.approved'),
    'verified-debt': t('invoiceStatus.notPaid'),
  };
  return statusMap[status] || status;
};

// ─── Booked invoice logic ─────────────────────────────────────────────────────
const proceedToNextStage = async () => {
  switch (activeFilter.value) {
    case 'approved':
      activeFilter.value = 'verified-debt';
      break;
    case 'verified-debt':
      activeFilter.value = SupplierInvoiceStatusType.Paid;
      break;
    case SupplierInvoiceStatusType.Paid:
      selectedIds.value = new Set();
      break;
    default:
      break;
  }
  await refetch();
  selectedIds.value = new Set(succeededIds.value.keys());
  succeededIds.value = new Map();
  clearAllFailedIds();
  resetUi();
};

const isVoucherResult = (result: unknown): result is ProperVoucherResult =>
  typeof result === 'object' &&
  result !== null &&
  typeof (result as ProperVoucherResult).voucher.VoucherNumber === 'number';

function toProcessingPanelInvoice(inv: Invoice): InvoiceProcessingPanelInvoice {
  return {
    ...inv,
    supplierInvoiceNumber: inv.serialNumber,
    accountType: inv.accounts.map((a) => `${a.number} - ${a.name}`).join(', '),
    pennyAdjust: 0,
    booked: inv.booked ?? false,
    paymentFileId: inv.paymentFileId ?? null,
    finalPayDate: inv.finalPayDate ?? null,
  };
}

const resolveProcessed = (id: string): Invoice | undefined =>
  invoices.value.find((i) => i.id === id) ?? processedInvoices.value.get(id);

const failedInvoices = computed(() =>
  [...failedIds.value.keys()]
    .map(resolveProcessed)
    .filter((i): i is Invoice => i !== undefined)
    .map(toProcessingPanelInvoice),
);

const succeededInvoices = computed(() =>
  [...succeededIds.value.keys()]
    .map(resolveProcessed)
    .filter((i): i is Invoice => i !== undefined)
    .map(toProcessingPanelInvoice),
);

const failedItems = computed(() =>
  [...failedIds.value.entries()].map(([id, error]) => ({
    id,
    label: resolveProcessed(id)?.serialNumber ?? id,
    errorMessages: getErrorMessage(error),
  })),
);

type ProcessResult = { success: boolean; error?: string; idList?: string[] };

const callSubmitPaymentFileIdApi = async (
  pFileId: string,
): Promise<ProcessResult> => {
  const { failureReason, supplierInvoiceIds } =
    await paymentFileService.submitPaymentFile(pFileId);

  if (failureReason) {
    return { success: false, error: failureReason, idList: [] };
  }

  isPaymentConfirmed.value = true;
  return { success: true, idList: supplierInvoiceIds };
};

const callUnSubmitPaymentFileIdApi = async (
  pFileId: string,
): Promise<ProcessResult> => {
  failedIds.value.clear();

  const { failureReason, supplierInvoiceIds } =
    await paymentFileService.unSubmitPaymentFile(pFileId);

  if (failureReason) {
    return { success: false, error: failureReason, idList: [] };
  }

  return { success: true, idList: supplierInvoiceIds };
};

const createPaymentFile = async (): Promise<void> => {
  isDownloading.value = true;

  try {
    const {
      blob,
      paymentFileId: id,
      paymentFileName: fileName,
    } = await paymentFileService.createPaymentFile({
      invoicesIds: selectedInvoices.value.map((inv) => inv.id),
      requestPaymentDate: paymentDate.value,
    });

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);

    paymentFileId.value = id;
    isPaymentFileDownloaded.value = true;
    void refetch();
  } catch (error) {
    console.error('Failed to download payment file %o', error);
  } finally {
    isDownloading.value = false;
  }
};

const downloadPaymentFileFor = async (pId: string) => {
  isDownloading.value = true;

  try {
    const {
      blob,
      paymentFileId: id,
      paymentFileName: fileName,
      supplierInvoiceIds: invoiceIds,
    } = await paymentFileService.getPaymentFile(pId);

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);

    paymentFileId.value = id;
    selectedIds.value = new Set(invoiceIds);
    isPaymentFileDownloaded.value = true;
    void refetch();
  } catch (error) {
    console.error('Failed to download payment file %o', error);
  } finally {
    isDownloading.value = false;
  }
};

const activatePaymentFor = async (pFileId: string) => {
  isDownloading.value = true;

  try {
    const { paymentFileId: id, supplierInvoiceIds: invoiceIds } =
      await paymentFileService.getPaymentFile(pFileId);

    paymentFileId.value = id;
    selectedIds.value = new Set(invoiceIds);
    isPaymentFileDownloaded.value = true;
    void refetch();
  } catch (error) {
    console.error('Failed to select invoices for payment file %o', error);
  } finally {
    isDownloading.value = false;
  }
};

const confirmPayment = async () => {
  isProcessing.value = true;
  if (!paymentFileId.value) {
    console.error('no payment file ID set: %o', paymentFileId.value);
    return;
  }

  const submitResult = await callSubmitPaymentFileIdApi(paymentFileId.value);

  if (!submitResult.success) {
    await callUnSubmitPaymentFileIdApi(paymentFileId.value);
  } else {
    await refetch();
  }

  isProcessing.value = false;
  isPaymentConfirmed.value = true;
};

const callVoucherApi = async (invoiceId: string): Promise<ProcessResult> => {
  try {
    const result = await voucherService.createVoucher(invoiceId);
    if (isVoucherResult(result)) {
      return { success: true };
    }

    const message =
      (result as { message?: string }).message ??
      t('adminSupplierInvoice.bookingInvoices.failedToCreateVoucher');
    return { success: false, error: message };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : t('adminSupplierInvoice.bookingInvoices.failedToCreateVoucher'),
    };
  }
};

const processOne = async (invoiceId: string): Promise<ProcessResult> => {
  const start = Date.now();
  const result = await callVoucherApi(invoiceId);
  const elapsed = Date.now() - start;
  const remaining = Math.max(0, 1000 - elapsed);

  if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
  return result;
};

const retryOne = async (invoiceId: string) => {
  isRetrying.value = true;
  retryingIds.value.add(invoiceId);
  retryingIds.value = new Set(retryingIds.value);

  const invoice = invoices.value.find((i) => i.id === invoiceId);
  if (invoice) processedInvoices.value.set(invoiceId, invoice);

  const result = await processOne(invoiceId);

  if (result.success) {
    failedIds.value.delete(invoiceId);
    failedIds.value = new Map(failedIds.value);
  } else {
    failedIds.value.set(invoiceId, result.error);
    failedIds.value = new Map(failedIds.value);
  }

  isRetrying.value = false;
  retryingIds.value.delete(invoiceId);
  retryingIds.value = new Set(retryingIds.value);
};

const getErrorMessage = (error: string | undefined): string => {
  if (!error) return 'Unknown error';
  try {
    const parsedError = JSON.parse(error);
    return parsedError.message ?? error;
  } catch {
    return error;
  }
};

const processFor = async (invoiceIds: string[]): Promise<boolean> => {
  if (invoiceIds.length === 0) return false;

  isProcessing.value = true;
  processedCount.value = 0;
  failedIds.value = new Map();

  for (const id of invoiceIds) {
    const invoice = invoices.value.find((i) => i.id === id);
    if (invoice) processedInvoices.value.set(id, invoice);
  }

  for (const id of invoiceIds) {
    const result = await processOne(id);
    if (!result.success) {
      failedIds.value.set(id, result.error);
      failedIds.value = new Map(failedIds.value);
    } else {
      succeededIds.value.set(id, result.error);
      succeededIds.value = new Map(succeededIds.value);
    }
    processedCount.value++;
  }

  isProcessing.value = false;
  isProcessingDone.value = true;

  await refetch();

  if (failedIds.value.size > 0) {
    return false;
  }

  return true;
};

const processForSelected = async (): Promise<boolean> => {
  return processFor(selectedInvoices.value.map((inv) => inv.id));
};

const unpaidInvoices = computed(() =>
  invoices.value.filter(
    (invoice) =>
      invoice.paymentFileId &&
      invoice.status === 'verified-debt' &&
      invoice.booked,
  ),
);

const unbookedPaymentFileIds = computed(() => {
  return new Set(
    [...unpaidInvoices.value]
      .map((invoice) => invoice.paymentFileId)
      .filter((i): i is number => i !== undefined),
  );
});

const unBookedPaidInvoices = computed(() =>
  invoices.value.filter(
    (invoice) => invoice.status === 'verified-debt' && !invoice.booked,
  ),
);

const selectedInvoices = computed(() =>
  filteredBookedInvoices.value.filter((inv) => selectedIds.value.has(inv.id)),
);

const isAllSelected = computed(() => {
  return (
    filteredBookedInvoices.value.length > 0 &&
    filteredBookedInvoices.value.every((inv) => selectedIds.value.has(inv.id))
  );
});

const isSomeSelected = computed(() =>
  filteredBookedInvoices.value.some((inv) => selectedIds.value.has(inv.id)),
);

const toggleAll = () => {
  if (isAllSelected.value) {
    filteredBookedInvoices.value.forEach((inv) =>
      selectedIds.value.delete(inv.id),
    );
  } else if (isSomeSelected.value) {
    filteredBookedInvoices.value.forEach((inv) =>
      selectedIds.value.delete(inv.id),
    );
  } else {
    filteredBookedInvoices.value.forEach((inv) =>
      selectedIds.value.add(inv.id),
    );
  }

  selectedIds.value = new Set(selectedIds.value);
};

const toggleSelection = (id: string) => {
  const next = new Set(selectedIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  selectedIds.value = next;
};

const columns: ColumnDef<AdminInvoice>[] = [
  {
    key: 'serialNumber',
    label: t('adminSupplierInvoice.tableHeaders.serialNumber'),
    sortable: true,
    render: (row) => row.serialNumber,
  },
  {
    key: 'project',
    label: t('adminSupplierInvoice.tableHeaders.project'),
    sortable: true,
    render: (row) => toProjectDisplayName(row.project),
  },
  {
    key: 'accounts',
    label: t('adminSupplierInvoice.tableHeaders.accountType'),
    sortable: true,
    render: (row) =>
      row.accounts.map((a) => `${a.number} - ${a.name}`).join(', '),
  },
  {
    key: 'invoiceNumber',
    label: t('adminSupplierInvoice.tableHeaders.invoiceNumber'),
    sortable: true,
    render: (row) => row.invoiceNumber,
  },
  {
    key: 'status',
    label: t('adminSupplierInvoice.tableHeaders.status'),
    sortable: true,
    render: (row) => fmtStatus(row.status),
    isBadge: true,
    badgeClass: (row) => row.status,
  },
  {
    key: 'supplier',
    label: t('adminSupplierInvoice.tableHeaders.supplier'),
    sortable: true,
    render: (row) => row.supplier,
  },
  {
    key: 'totalAmount',
    label: t('adminSupplierInvoice.tableHeaders.totalAmount'),
    sortable: true,
    render: (row) => fmtCurrency(row.totalAmount),
    compareValue: (row) => fmtCurrency(row.totalAmount),
  },
  {
    key: 'vat',
    label: t('adminSupplierInvoice.tableHeaders.vat'),
    sortable: true,
    render: (row) => row.vat.toString(),
  },
  {
    key: 'invoiceDate',
    label: t('adminSupplierInvoice.tableHeaders.invoiceDate'),
    sortable: true,
    render: (row) => row.invoiceDate,
    compareValue(row) {
      return new Date(row.invoiceDate).getTime();
    },
  },
  {
    key: 'dueDate',
    label: t('adminSupplierInvoice.tableHeaders.dueDate'),
    sortable: true,
    render: (row) => row.dueDate,
    compareValue(row) {
      return new Date(row.dueDate).getTime();
    },
  },
  {
    key: 'paymentFileId',
    label: t('adminSupplierInvoice.tableHeaders.paymentFileId'),
    sortable: true,
    render: (row) => String(row.paymentFileId ?? '-'),
  },
] as const;

const filteredBookedInvoices = computed<AdminInvoicesData>(() => {
  let result = invoices.value;

  switch (activeFilter.value) {
    case 'approved':
      result = result.filter((invoice) => invoice.status === 'approved');
      break;
    case 'verified-debt':
      result = result.filter((invoice) => invoice.status === 'verified-debt');
      break;
    case SupplierInvoiceStatusType.Paid:
      result = result.filter(
        (invoice) => invoice.status === SupplierInvoiceStatusType.Paid,
      );
      break;
  }

  const query = searchQuery.value?.toLowerCase().trim();
  if (query) {
    result = result.filter((invoice) => {
      return (
        toProjectDisplayName(invoice.project).toLowerCase().includes(query) ||
        invoice.accounts.some((a) =>
          `${a.number} - ${a.name}`.toLowerCase().includes(query),
        ) ||
        invoice.supplier.toLowerCase().includes(query) ||
        invoice.approver.toLowerCase().includes(query) ||
        invoice.serialNumber.toLowerCase().includes(query)
      );
    });
  }

  return result;
});

const resetUi = () => {
  processedCount.value = 0;
  // Safe to drop: this zeroes processedCount, which hides the results panel.
  processedInvoices.value = new Map();
  isProcessingDone.value = false;
  isRetrying.value = false;
  retryingIds.value = new Set();
  isDownloading.value = false;
  isPaymentFileDownloaded.value = false;
  isPaymentConfirmed.value = false;
  paymentFileId.value = '';
};

const clearAllFailedIds = () => {
  resetUi();
  failedIds.value = new Map();
};

const reloadAll = () => {
  clearAllFailedIds();
  void refetch();
};

onActivated(() => {
  if (failedInvoices.value.length === 0) {
    resetUi();
  }
  void refetch();
});
</script>

<template>
  <div v-if="!hide.fortnoxAccountHandler" class="fortnox-handler-wrapper">
    <FortnoxAccountHandler />
  </div>
  <div class="invoice-table-wrapper">
    <div class="top-row-controls">
      <div class="top-header-row">
        <div class="search-field">
          <input
            class="input search"
            type="search"
            :placeholder="t('search') + '...'"
            v-model="searchInput"
          />
        </div>
        <router-link
          v-if="!props.filter?.supplier"
          :to="{ name: RouteName.AdminCreateInvoices }"
          class="btn-new-invoice"
        >
          <PhPlus :size="16" weight="bold" />
          {{ t('adminSupplierInvoice.newInvoice') }}
        </router-link>
        <button
          v-if="activeFilter !== 'incoming'"
          type="button"
          class="btn-export-csv"
          @click="showExportModal = true"
        >
          <PhDownloadSimple :size="16" weight="bold" />
          {{ t('adminSupplierInvoice.export.openButton') }}
        </button>
      </div>
      <div class="top-controls-row">
        <div class="button-row">
          <button
            v-if="!props.hide?.mailboxSync"
            class="btn-incoming"
            :class="{ active: activeFilter === 'incoming' }"
            @click="setFilter(SupplierInvoiceStatusType.Incoming)"
          >
            {{ t('adminSupplierInvoice.status.incoming') }}
            <PhEnvelope :size="18" />
          </button>
          <button
            class="btn-white"
            :class="{ active: activeFilter === 'all' }"
            @click="setFilter('all')"
          >
            {{ t('adminSupplierInvoice.status.all') }}
          </button>
          <button
            class="btn-undercertificate"
            :class="{
              active:
                activeFilter === SupplierInvoiceStatusType.UnderCertificate,
            }"
            @click="setFilter(SupplierInvoiceStatusType.UnderCertificate)"
          >
            {{ t('adminSupplierInvoice.status.undercertificate') }}
          </button>
          <button
            class="btn-parked"
            :class="{
              active: activeFilter === SupplierInvoiceStatusType.Parked,
            }"
            @click="setFilter(SupplierInvoiceStatusType.Parked)"
          >
            {{ t('adminSupplierInvoice.status.parked') }}
          </button>
          <button
            class="btn-disputed"
            :class="{
              active: activeFilter === SupplierInvoiceStatusType.Disputed,
            }"
            @click="setFilter(SupplierInvoiceStatusType.Disputed)"
          >
            {{ t('adminSupplierInvoice.status.disputed') }}
          </button>
          <button
            class="btn-voided"
            :class="{
              active: activeFilter === SupplierInvoiceStatusType.Voided,
            }"
            @click="setFilter(SupplierInvoiceStatusType.Voided)"
          >
            {{ t('adminSupplierInvoice.status.voided') }}
          </button>
          <button
            class="btn-approved"
            :class="{
              active: activeFilter === SupplierInvoiceStatusType.Approved,
            }"
            @click="setFilter(SupplierInvoiceStatusType.Approved)"
          >
            {{ t('adminSupplierInvoice.status.approved') }}
          </button>
          <button
            class="btn-verified-debt"
            :class="{ active: activeFilter === 'verified-debt' }"
            @click="setFilter('verified-debt')"
          >
            {{ t('adminSupplierInvoice.status.verified-debt') }}
          </button>
          <button
            class="btn-verified-payed"
            :class="{ active: activeFilter === SupplierInvoiceStatusType.Paid }"
            @click="setFilter(SupplierInvoiceStatusType.Paid)"
          >
            {{ t('adminSupplierInvoice.status.verified-payed') }}
          </button>
        </div>
        <span v-if="isFetching" class="spinner" />
      </div>
    </div>

    <div
      v-if="isBookedFilter && !hide.invoiceProcessing"
      class="processing-panel"
    >
      <InvoiceProcessingPanel
        :activeFilter="activeFilter as BookedFilterStatus"
        :isProcessing="isProcessing"
        :isProcessingDone="isProcessingDone"
        :isRetrying="isRetrying"
        :retryingIds="retryingIds"
        :isDownloading="isDownloading"
        :isPaymentFileDownloaded="isPaymentFileDownloaded"
        :isPaymentConfirmed="isPaymentConfirmed"
        :processedCount="processedCount"
        :failedItems="failedItems"
        :selectedCount="selectedInvoices.length"
        :failedInvoices="failedInvoices"
        :succeededInvoices="succeededInvoices"
        v-model:paymentDate="paymentDate"
        @process="processForSelected"
        @retryOne="retryOne"
        @downloadFile="createPaymentFile"
        @confirmPayment="confirmPayment"
        @clearAllErrors="clearAllFailedIds"
        @reloadAll="reloadAll"
        @proceedToNextStage="proceedToNextStage"
      />
    </div>

    <div v-if="activeFilter === 'incoming'">
      <admin-invoice-inbox />
    </div>

    <AllInvoicesTable
      v-else-if="activeFilter === 'all' || activeFilter === undefined"
      :rows="invoices"
      :total-rows="totalInvoices"
      :filters="currentTableQuery"
      @query-change="onTableQueryChange"
      @row-click="navigateToInvoice($event.id)"
      @navigate-to-project="navigateToProjectInvoice($event.id)"
      :is-loading="isFetching"
    />
    <PaidInvoiceTable
      v-else-if="activeFilter === SupplierInvoiceStatusType.Paid"
      :selected-ids="selectedIds"
      :total-rows="totalInvoices"
      :rows="invoices"
      :filters="currentTableQuery"
      :is-loading="isFetching"
      @query-change="onTableQueryChange"
      @toggle-selection="toggleSelection"
      @toggle-all="toggleAll"
      @row-click="navigateToInvoice($event.id)"
      @navigate-to-invoice="navigateToInvoice($event.id)"
    />
    <SpecificInvoiceTable
      v-else-if="!isBookedFilter"
      :rows="invoices"
      :total-rows="totalInvoices"
      :filters="currentTableQuery"
      :is-loading="isFetching"
      @query-change="onTableQueryChange"
      @row-click="navigateToInvoice($event.id)"
      @navigate-to-project="navigateToProjectInvoice($event.id)"
    />

    <template v-else>
      <div v-if="activeFilter === 'verified-debt'" class="unpaid">
        <div v-for="pFileId in unbookedPaymentFileIds" :key="pFileId">
          <div class="selection-bar">
            <span>
              {{ t('adminSupplierInvoice.bookingInvoices.nonBookedFor') }}
              {{ t('adminSupplierInvoice.tableHeaders.paymentFileId') }} :
              {{ pFileId }}.
              {{
                t('adminSupplierInvoice.bookingInvoices.downloadToVerifyAgain')
              }}.</span
            >
            <div class="buttons">
              <button @click="downloadPaymentFileFor(String(pFileId))">
                {{
                  t('adminSupplierInvoice.bookingInvoices.downloadPaymentFile')
                }}
                {{ pFileId }}
              </button>
              <button @click="activatePaymentFor(String(pFileId))">
                {{
                  t(
                    'adminSupplierInvoice.bookingInvoices.verifyPaymentForPaymentFile',
                  )
                }}
                {{ pFileId }}
              </button>
            </div>
          </div>
          <CESGTable
            :isShowHeader="true"
            :isSelectable="false"
            :columns="columns"
            :rows="
              unpaidInvoices.filter((inv) => inv.paymentFileId === pFileId)
            "
            :isAllSelected="isAllSelected"
            :isSomeSelected="isSomeSelected"
            :isProcessingDone="isProcessingDone"
            :failedIds="failedIds"
            :selectedIds="new Set()"
            :rowKey="(row) => row.id"
            :rowClass="
              (row) => [
                selectedIds.has(row.id) ? 'selected' : '',
                failedIds.has(row.id) ? 'failed' : '',
              ]
            "
          />
        </div>
        <div v-if="unBookedPaidInvoices.length > 0 && failedIds.size === 0">
          <div class="selection-bar">
            <span>
              {{ t('adminSupplierInvoice.bookingInvoices.nonBookedPaidFor') }}
              {{
                t(
                  'adminSupplierInvoice.bookingInvoices.pressButtonToVerifyPayment',
                )
              }}.</span
            >
          </div>
          <CESGTable
            :isShowHeader="true"
            :isSelectable="false"
            :columns="columns"
            :rows="unBookedPaidInvoices"
            @navigateToItem="navigateToInvoice($event.id)"
          >
            <template #subColumn="{ item }">
              <button
                v-if="item"
                class="btn btn-retry"
                @click="processFor([item.id])"
              >
                {{
                  retryingIds.has(item.id)
                    ? '...'
                    : t('adminSupplierInvoice.bookingInvoices.tryVerifyAgain')
                }}
              </button>
            </template>
          </CESGTable>
        </div>
      </div>

      <div class="selection-bar">
        <span
          >{{ t('adminSupplierInvoice.bookingInvoices.chosen') }}:
          {{ selectedInvoices.length }}</span
        >
      </div>

      <div :class="{ 'is-processing': isProcessing }">
        <SelectableInvoiceTable
          :selected-ids="selectedIds"
          :total-rows="totalInvoices"
          :rows="invoices"
          :filters="currentTableQuery"
          :is-loading="isFetching"
          @query-change="onTableQueryChange"
          @toggleSelection="toggleSelection"
          @toggleAll="toggleAll"
          @row-click="navigateToInvoice($event.id)"
        />
      </div>
    </template>

    <div v-if="isProcessing && isBookedFilter" class="processing-overlay">
      <div class="processing-card">
        <p class="processing-title">
          {{ t('adminSupplierInvoice.bookingInvoices.processing') }}
        </p>
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{
              width: `${(processedCount / selectedInvoices.length) * 100}%`,
            }"
          />
        </div>
        <p class="progress-label">
          {{ processedCount }} / {{ selectedInvoices.length }}
        </p>
      </div>
    </div>

    <AdminSupplierInvoiceExportModal
      v-if="showExportModal"
      :active-filter="activeFilter"
      @close="showExportModal = false"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/style/variables';
@use 'bulma/sass/utilities/initial-variables' as *;
@use '@/assets/style/popup';

.fortnox-handler-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 4px solid var(--inactive-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.is-processing {
  opacity: 0.4;
  pointer-events: none;
  user-select: none;
  filter: grayscale(40%);
  transition:
    opacity 0.25s ease,
    filter 0.25s ease;
}

.processing-panel {
  margin: 1rem 0;
}

.processing-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.processing-card {
  background: white;
  border: 1px solid var(--light-grey);
  border-radius: 10px;
  padding: 20px 32px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  min-width: 260px;
  text-align: center;

  .processing-title {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--primary-color);
    margin-bottom: 12px;
  }

  .progress-track {
    width: 100%;
    height: 8px;
    background: var(--light-grey);
    border-radius: 99px;
    overflow: hidden;
    margin-bottom: 8px;

    .progress-fill {
      height: 100%;
      background: var(--inactive-color);
      border-radius: 99px;
      transition: width 0.4s ease;
    }
  }

  .progress-label {
    font-size: 0.8rem;
    color: var(--inactive-color);
  }
}

.unpaid {
  margin-bottom: 16px;
}

.btn-retry {
  height: 28px;
  padding: 0 0.75rem;
  font-size: 0.85rem;
  background-color: var(--primary-color);
  color: white;
  transition: all 0.2s;

  &:hover {
    background-color: color-mix(in srgb, var(--primary-color) 85%, white);
    box-shadow: 0 2px 8px rgba(0, 28, 116, 0.4);
    color: white;
  }
}

.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--inactive-color);

  .buttons {
    display: flex;
    gap: 8px;

    button {
      padding: 6px 12px;
      border: 1px solid #ccc;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s ease-in-out;

      &:hover {
        opacity: 0.9;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.invoice-table-wrapper {
  .top-row-controls {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin-bottom: 16px;
    gap: 8px;
  }

  .top-header-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    @media screen and (max-width: $desktop) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .top-controls-row {
    display: flex;
    align-items: center;
    gap: 16px;

    @media screen and (max-width: $desktop) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .search-field {
    .input.search {
      width: 300px;
      padding: 8px 12px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background-color: white;

      @media screen and (max-width: $desktop) {
        width: 100%;
        margin-bottom: 16px;
      }
    }
  }

  .btn-new-invoice {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 8px 24px;
    border-radius: 9999px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    background-color: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    text-decoration: none;
    text-align: center;
    line-height: normal;
    font-size: 14px;

    &:hover {
      background-color: color-mix(in srgb, var(--primary-color) 85%, white);
      box-shadow: 0 2px 8px rgba(0, 28, 116, 0.4);
      color: white;
      text-decoration: none;
    }

    &:active {
      box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
    }
  }

  .btn-export-csv {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 8px 24px;
    border-radius: 9999px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    background-color: white;
    color: var(--primary-color);
    border: 1px solid var(--primary-color);
    text-align: center;
    line-height: normal;
    font-size: 14px;

    &:hover {
      background-color: color-mix(in srgb, var(--primary-color) 8%, white);
      box-shadow: 0 2px 8px rgba(0, 28, 116, 0.2);
    }

    &:active {
      box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
    }
  }

  .btn-sync-invoice-documents {
    margin-left: auto;
    background-color: #e3f2fd;
    border-color: #90caf9;
  }

  .btn-delete {
    display: flex;
    padding: 6px 12px;
    border: 1px solid #ccc;
    cursor: pointer;
    border-radius: 4px;

    background-color: #fdeaea;
    color: #b33939;
  }

  .btn-delete:hover {
    background-color: #f3c9c9;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
  }

  .button-row {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;

    @media screen and (max-width: $desktop) {
      width: 100%;
      padding-bottom: 8px;
    }

    button {
      --dot-color: transparent;

      &:first-child {
        margin-right: 1rem;
      }
      &:last-child {
        margin-right: 1rem;
      }

      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      padding: 9px 10px 9px 12px;
      border: 1px solid #ccc;
      cursor: pointer;
      background-color: white;
      color: black;
      line-height: 1;
      // Needed due to the math being wierd when doing fully rounded corners
      border-radius: 3000px 9999px 9999px 3000px;
      transition: all 0.2s ease-in-out;

      &::after {
        content: '';
        display: inline-block;
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: var(--dot-color);
      }

      &.active {
        background-color: var(--dot-color);
        border-color: #007bff;
        font-weight: bold;
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.4);
      }

      &:hover {
        opacity: 0.9;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
      }
    }

    .btn-white {
      padding-right: 75px;
      &::after {
        display: none;
      }
      &.active {
        background-color: #f0f0f0;
      }
    }

    .btn-incoming {
      &::after {
        display: none;
      }
      &.active {
        background-color: #f0f0f0;
      }
    }

    .btn-booked {
      --dot-color: var(--invoice-approved);
    }

    .btn-undercertificate {
      --dot-color: var(--invoice-undercertificate);
    }

    .btn-parked {
      --dot-color: var(--invoice-parked);
    }

    .btn-disputed {
      --dot-color: var(--invoice-disputed);
    }

    .btn-voided {
      --dot-color: var(--invoice-voided);
    }

    .btn-approved {
      --dot-color: var(--invoice-approved);
    }

    .btn-verified-debt {
      --dot-color: var(--invoice-verified-debt);
    }

    .btn-verified-payed {
      --dot-color: var(--invoice-paid);
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: white;
  padding: 1.4rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  text-align: center;
  border: 1px solid #e2e8f0;
  animation: modalFadeIn 0.2s ease-out;
  min-width: 50% !important;
  max-width: 80% !important;
  min-height: 10% !important;
  max-height: 50% !important;

  table {
    background-color: white;
    border-collapse: separate;
    justify-self: center;

    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

    thead {
      tr {
        th {
          border-bottom: 2px solid #ddd;
          text-align: left;
          padding: 0.75rem;
          font-weight: 600;
        }
      }
    }

    tbody {
      background-color: #f4f5f6;

      tr {
        td {
          text-align: left;
          padding: 0.75rem;
          text-align: left;
          border: none;
          font-size: 0.9em;
        }
      }
    }
  }
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-title {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.modal-hint {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.5rem;
}

.modal-text {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.modal-actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.btn-modal-secondary,
.btn-modal-danger {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-modal-secondary {
  background-color: #f1f5f9;
  color: #475569;

  &:hover {
    background-color: #e2e8f0;
    color: #1e293b;
  }
}

.btn-modal-danger {
  background-color: #ef4444;
  color: white;

  &:hover {
    background-color: #dc2626;
  }
}
</style>
